import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { getCurrency, setCurrencyPreference } from '../utils/currency';

export const useCurrency = () => {
  const [currency, setCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch currency setting from backend on component mount
  useEffect(() => {
    fetchCurrency();
  }, []);

  const fetchCurrency = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    
    try {
      // Try to get currency from backend first
      const response = await apiClient.get('/settings/currency', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data && response.data.currency) {
        setCurrency(response.data.currency);
        // Also update localStorage to keep in sync
        setCurrencyPreference(response.data.currency);
      } else {
        // Fallback to utility function if backend doesn't have currency
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching currency from backend:', err);
      
      // Fallback to utility function if API fails
      try {
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
        setError(null);
      } catch (utilError) {
        console.error('Error getting currency from utility:', utilError);
        setError('Failed to load currency setting');
        setCurrency('USD'); // Ultimate fallback
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateCurrency = async (newCurrency) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    
    try {
      // Update backend first
      await apiClient.patch('/settings/currency', 
        { currency: newCurrency }, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Update local state
      setCurrency(newCurrency);
      
      // Update localStorage
      setCurrencyPreference(newCurrency);
      
      // Broadcast currency change event for other components
      const currencyEvent = new CustomEvent('currencyChanged', { 
        detail: { currency: newCurrency } 
      });
      window.dispatchEvent(currencyEvent);
      
      setError(null);
      return { success: true, message: 'Currency updated successfully' };
      
    } catch (err) {
      console.error('Error updating currency:', err);
      setError(err.response?.data?.detail || 'Failed to update currency');
      return { success: false, message: err.response?.data?.detail || 'Failed to update currency' };
    } finally {
      setIsLoading(false);
    }
  };

  // Listen for currency changes from other components
  useEffect(() => {
    const handleCurrencyChange = (event) => {
      if (event.detail && event.detail.currency) {
        setCurrency(event.detail.currency);
      }
    };

    window.addEventListener('currencyChanged', handleCurrencyChange);
    
    return () => {
      window.removeEventListener('currencyChanged', handleCurrencyChange);
    };
  }, []);

  return { 
    currency, 
    isLoading, 
    error, 
    updateCurrency, 
    refreshCurrency: fetchCurrency 
  };
};

export default useCurrency; 